import React, { useState } from 'react';
import { Inbox, TreePine, LayoutDashboard, Users, Image, RefreshCw } from 'lucide-react';
import { Navbar } from '../Header/Navbar';
import { getAdapter } from '../../lib/adapters';
import type { MemberSubmission, FamilyTreeRecord } from '../../lib/adapters';
import { useDashboardStore } from '../../store/dashboardStore';
import { SubmissionReview } from './SubmissionReview';
import { MembersManager } from './MembersManager';
import { GalleryManager } from './GalleryManager';
import { pushRoute } from '../../utils/routing';

type Tab = 'submissions' | 'members' | 'gallery' | 'trees' | 'overview';
type StatusFilter = 'all' | MemberSubmission['status'];

const STATUS_LABELS: Record<MemberSubmission['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const AdminDashboard: React.FC = () => {
  const [tab, setTab] = useState<Tab>('submissions');
  const [submissions, setSubmissions] = useState<MemberSubmission[]>([]);
  const [trees, setTrees] = useState<FamilyTreeRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [reviewing, setReviewing] = useState<MemberSubmission | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchTrees } = useDashboardStore();

  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const adapter = getAdapter();
      const data = await adapter.listSubmissions();
      setSubmissions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadTrees = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchTrees();
      setTrees(useDashboardStore.getState().familyTrees);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trees');
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    setReviewing(null);
    if (next === 'submissions') loadSubmissions();
    if (next === 'trees') loadTrees();
  };

  React.useEffect(() => {
    // Load trees once on mount so the Members tab has a tree selector,
    // and refresh submissions when the tab is opened.
    loadTrees();
    if (tab === 'submissions') loadSubmissions();
    if (tab === 'trees') loadTrees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (tab === 'submissions') loadSubmissions();
    if (tab === 'trees') loadTrees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const filtered = statusFilter === 'all'
    ? submissions
    : submissions.filter(s => s.status === statusFilter);

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  const renderTabButton = (id: Tab, label: string, icon: React.ReactNode, badge?: number) => (
    <button
      onClick={() => switchTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        tab === id ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {icon}
      {label}
      {badge !== undefined && badge > 0 && (
        <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${tab === id ? 'bg-white/20' : 'bg-red-100 text-red-600'}`}>
          {badge}
        </span>
      )}
    </button>
  );

  const renderTreeRow = (tree: FamilyTreeRecord) => (
    <div key={tree.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
          <TreePine className="w-4 h-4 text-green-700" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{tree.name}</p>
          <p className="text-xs text-gray-500">
            {tree.memberCount} members · {tree.generationCount} generations
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => pushRoute(`/admin/family-tree/${tree.id}`)}
          className="px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 rounded-lg transition-colors"
        >
          Edit
        </button>
        <button
          onClick={() => pushRoute(`/family-tree/${tree.id}`)}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          View
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
            <p className="text-gray-500">Manage the whole app — submissions, trees, and more.</p>
          </div>
          {loading && <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-gray-200 pb-3 flex-wrap">
          {renderTabButton('submissions', 'Submissions', <Inbox className="w-4 h-4" />, pendingCount)}
          {renderTabButton('members', 'Members', <Users className="w-4 h-4" />)}
          {renderTabButton('gallery', 'Gallery', <Image className="w-4 h-4" />)}
          {renderTabButton('trees', 'Trees', <TreePine className="w-4 h-4" />)}
          {renderTabButton('overview', 'Overview', <LayoutDashboard className="w-4 h-4" />)}
        </div>

        {error && (
          <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
        )}

        {tab === 'submissions' && !reviewing && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Member Submissions</h2>
              <div className="flex items-center gap-1">
                {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map(f => (
                  <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      statusFilter === f
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f === 'all' ? 'All' : STATUS_LABELS[f]}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className="px-4 py-12 text-center text-gray-400">Loading submissions…</p>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-12 text-center text-gray-400">
                {statusFilter === 'pending'
                  ? 'No pending submissions. Nice!'
                  : 'No submissions yet.'}
              </p>
            ) : (
              <div>
                {filtered.map(s => {
                  const treeName = trees.find(t => t.id === s.treeId)?.name ?? s.treeId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setReviewing(s)}
                      className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          s.status === 'pending' ? 'bg-amber-400'
                          : s.status === 'approved' ? 'bg-green-500'
                          : 'bg-red-400'
                        }`} />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{s.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {treeName}
                            {s.relationship && ` · ${s.relationship.type} of a member`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          s.status === 'pending' ? 'bg-amber-100 text-amber-700'
                          : s.status === 'approved' ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-600'
                        }`}>
                          {STATUS_LABELS[s.status]}
                        </span>
                        <span className="text-xs text-gray-400">
                          {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'submissions' && reviewing && (
          <SubmissionReview
            submission={reviewing}
            onBack={() => setReviewing(null)}
            onResolved={(updated) => {
              setSubmissions(prev => prev.map(s => s.id === updated.id ? updated : s));
              setReviewing(null);
            }}
          />
        )}

        {tab === 'members' && (
          <MembersManager trees={trees} />
        )}

        {tab === 'gallery' && (
          <GalleryManager trees={trees} />
        )}

        {tab === 'trees' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">All Family Trees</h2>
            </div>
            {loading ? (
              <p className="px-4 py-12 text-center text-gray-400">Loading trees…</p>
            ) : trees.length === 0 ? (
              <p className="px-4 py-12 text-center text-gray-400">No trees found.</p>
            ) : (
              <div>
                {trees.map(renderTreeRow)}
              </div>
            )}
          </div>
        )}

        {tab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <p className="text-sm text-gray-500">Total Trees</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{trees.length}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <p className="text-sm text-gray-500">Pending Submissions</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{pendingCount}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <p className="text-sm text-gray-500">Total Submissions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{submissions.length}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
